<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { debounce } from '@chatwoot/utils';
import { OnClickOutside } from '@vueuse/components';

import ComboBoxDropdown from 'dashboard/components-next/combobox/ComboBoxDropdown.vue';
import { createContactSearcher } from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  excludedContactIds: {
    type: Array,
    default: () => [],
  },
  max: {
    type: Number,
    default: 0,
  },
  placeholder: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const searchContacts = createContactSearcher();

const open = ref(false);
const search = ref('');
const isSearching = ref(false);
const options = ref([]);
const labelsById = ref({});

const MIN_SEARCH_LENGTH = 2;

const contactLabel = ({ name, phoneNumber, email }) => {
  const handle = phoneNumber || email;
  return handle ? `${name} (${handle})` : name;
};

const selectedTags = computed(() =>
  props.modelValue.map(id => ({
    value: id,
    label: labelsById.value[id] || String(id),
  }))
);

const isAtLimit = computed(
  () => props.max > 0 && props.modelValue.length >= props.max
);

const emptyState = computed(() =>
  search.value.trim().length < MIN_SEARCH_LENGTH
    ? t('GROUPS.PICKER.HINT')
    : t('GROUPS.PICKER.EMPTY')
);

const onSearch = debounce(
  async query => {
    isSearching.value = true;
    try {
      const results = await searchContacts(query);
      // null means the request was aborted by a newer search
      if (results === null) return;
      options.value = results
        .filter(contact => !props.excludedContactIds.includes(contact.id))
        .map(contact => ({
          value: contact.id,
          label: contactLabel(contact),
        }));
      isSearching.value = false;
    } catch {
      isSearching.value = false;
    }
  },
  400,
  false
);

const toggleDropdown = () => {
  if (props.disabled) return;
  open.value = !open.value;
};

const toggleContact = option => {
  const contactIds = [...props.modelValue];
  const index = contactIds.indexOf(option.value);

  if (index === -1) {
    if (isAtLimit.value) return;
    contactIds.push(option.value);
    labelsById.value = {
      ...labelsById.value,
      [option.value]: option.label,
    };
  } else {
    contactIds.splice(index, 1);
  }

  emit('update:modelValue', contactIds);
};

const removeContact = contactId => {
  emit(
    'update:modelValue',
    props.modelValue.filter(id => id !== contactId)
  );
};
</script>

<template>
  <div class="relative w-full min-w-0">
    <OnClickOutside @trigger="open = false">
      <div
        class="flex flex-wrap w-full min-h-[42px] gap-2 px-3 py-2.5 border rounded-lg bg-n-alpha-black2 transition-all duration-500 ease-in-out cursor-pointer"
        :class="{
          'border-n-brand': open,
          'border-n-weak hover:border-n-slate-6': !open,
          'pointer-events-none opacity-50': disabled,
        }"
        @click="toggleDropdown"
      >
        <div
          v-for="tag in selectedTags"
          :key="tag.value"
          class="flex items-center justify-center max-w-full gap-1 px-2 py-0.5 rounded-lg bg-n-alpha-black1"
          @click.stop
        >
          <span class="flex-grow min-w-0 text-sm truncate text-n-slate-12">
            {{ tag.label }}
          </span>
          <span
            class="flex-shrink-0 cursor-pointer i-lucide-x size-3 text-n-slate-11"
            @click="removeContact(tag.value)"
          />
        </div>
        <span
          v-if="!selectedTags.length"
          class="flex items-center text-sm text-n-slate-11"
        >
          {{ placeholder || t('GROUPS.PICKER.PLACEHOLDER') }}
        </span>
      </div>

      <ComboBoxDropdown
        v-model:search-value="search"
        :open="open"
        :options="options"
        :loading="isSearching"
        multiple
        :selected-values="modelValue"
        :search-placeholder="t('GROUPS.PICKER.SEARCH_PLACEHOLDER')"
        :empty-state="emptyState"
        @search="onSearch"
        @select="toggleContact"
      />
    </OnClickOutside>
    <div v-if="max" class="flex justify-end mt-1 text-xs text-n-slate-11">
      <span v-if="isAtLimit" class="text-n-amber-11">
        {{ t('GROUPS.PICKER.LIMIT_REACHED', { max }) }}
      </span>
      <span v-else>
        {{ t('GROUPS.PICKER.LIMIT', { count: modelValue.length, max }) }}
      </span>
    </div>
  </div>
</template>
